import { type AsyncThunk, createAsyncThunk } from '@reduxjs/toolkit';

import type { AsyncThunkActionDefinition, ControllerConfig, PageReducerState } from './types';

export default function createFetchDataAction<TState>(
  input: ControllerConfig<TState>,
): AsyncThunkActionDefinition {
  const asyncThunk = createAsyncThunk(`${input.name}/fetchData`, async (params) => {
    if (input.fetchData) {
      const pageData = await input.fetchData(
        params as Parameters<NonNullable<typeof input.fetchData>>[0],
      );
      return { params, pageData };
    } else {
      return { params, pageData: {} };
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }) as AsyncThunk<any, any, object>;

  return {
    asyncThunk,
    pending: (state) => {
      (state as PageReducerState<TState>).pageState.fetching = true;
    },
    onSuccess: (state, action) => {
      const s = state as PageReducerState<TState>;
      const a = action as {
        payload: {
          params: { location: unknown; params: unknown; queryParams: unknown };
          pageData: object;
        };
      };
      s.pageState.fetching = false;
      s.pageState.loaded = true;
      s.pageState.location = a.payload.params
        .location as PageReducerState<TState>['pageState']['location'];
      s.pageState.params = a.payload.params
        .params as PageReducerState<TState>['pageState']['params'];
      s.pageState.queryParams = a.payload.params
        .queryParams as PageReducerState<TState>['pageState']['queryParams'];

      s.data = {
        ...(s.data as object),
        ...a.payload.pageData,
      } as TState;
    },
    onError: (state, action) => {
      const s = state as PageReducerState<TState>;
      const a = action as { meta?: { arg?: { location?: unknown } } } & { error?: unknown };
      // Narrow the log shape — the raw RTK action carries `meta.arg` which
      // includes `appState` for many controllers, leaking selected redux
      // state into production console output. Surface only the controller
      // name + the thrown error.
      console.error('Error fetching data', { name: input.name, error: a.error });
      s.pageState.errorOnLoad = true;
      s.pageState.fetching = false;
      s.pageState.loaded = true;
      s.pageState.location = (a?.meta?.arg?.location ??
        null) as PageReducerState<TState>['pageState']['location'];
    },
  };
}
